import React, { useState } from 'react';
import { Modal, Radio, Checkbox, Divider, Input } from 'antd';
import styled from '@emotion/styled';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useQuery } from '@tanstack/react-query';
import { errorModal } from 'vl-common/src/components/Modals';
import { useTranslation } from 'react-i18next';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';

// prettier-ignore
const SMILEY_OPTIONS = [
    { value: 'very_positive', src: 'https://assets.virtuallucyweb.co.uk/img/survey/rate-faces-5.png', alt: 'Very Positive' },
    { value: 'positive', src: 'https://assets.virtuallucyweb.co.uk/img/survey/rate-faces-4.png', alt: 'Positive' },
    { value: 'neutral', src: 'https://assets.virtuallucyweb.co.uk/img/survey/rate-faces-3.png', alt: 'Neutral' },
    { value: 'negative', src: 'https://assets.virtuallucyweb.co.uk/img/survey/rate-faces-2.png', alt: 'Negative' },
    { value: 'very_negative', src: 'https://assets.virtuallucyweb.co.uk/img/survey/rate-faces-1.png', alt: 'Very Negative' }
  ];

const AI_ISSUE_OPTIONS = [
  { value: 'transcription', label: 'Transcription accuracy' },
  { value: 'notes', label: 'Notes accuracy' },
  { value: 'tone', label: 'Notes tone' },
  { value: 'flags', label: 'Red flags not captured' },
  { value: 'quality', label: 'Notes generation quality' }
];

const WORKFLOW_IMPACT_OPTIONS = [
  { value: 'none', label: 'Not at all' },
  { value: 'minor', label: 'Minor edits needed' },
  { value: 'major', label: 'Major edits needed' }
];

const TIME_SAVED_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'unsure', label: 'Not sure' }
];

const { TextArea } = Input;

// Prevents Ant Design from truncating or wrapping text early
const FullWidthFormItem = styled(SessionFormItem)`
  width: 100%;

  .ant-form-item-label {
    width: 100% !important;
    max-width: 100% !important;
    text-align: left !important;
    white-space: normal !important;
  }

  .ant-form-item-label > label {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
`;

const StyledDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0px 16px;
`;

const SmileyGroup = styled(Radio.Group)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 0;
  margin: 6px 0 0 0;
  min-height: 40px;

  .ant-radio-wrapper {
    margin-right: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
  }

  .ant-radio {
    position: absolute;
    opacity: 0;
  }

  .smiley-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    width: 64px;
    height: 64px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
  }

  .ant-radio-wrapper-checked .smiley-icon {
    transform: scale(1.5);
    filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.15));
  }

  .ant-radio-wrapper:hover .smiley-icon {
    transform: scale(1.2);
  }

  &:has(.ant-radio-wrapper-checked) .ant-radio-wrapper:not(.ant-radio-wrapper-checked) {
    opacity: 0.5;
    filter: grayscale(0.2);
  }
`;

const ReportCallIssueModal = ({
  open,
  onClose,
  isVideoSessionType,
  appointment
}: {
  open: boolean;
  onClose: () => void;
  isVideoSessionType: boolean;
  appointment: any;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const sendTwilioEvents = useSendTwilioEvents();

  const heidiEnabled = useHeidiEnabled(appointment.appointment_id);
  const { sessionId } = useHeidiSession();
  const [reportType, setReportType] = useState<'call' | 'ai' | undefined>(undefined);

  const effectiveReportType = heidiEnabled ? reportType : 'call';

  const { data: savedCallReport, refetch } = useQuery({
    queryKey: ['savedCallReport', appointment.appointment_id],
    queryFn: async () => {
      const res = await dispatch(actions.getTwilioEvents(appointment.appointment_id, 'CLINICIAN', 'RELIABILITY'));
      if (res?.events?.length) {
        return JSON.parse(res.events[0]?.message);
      }
      return null;
    }
  });
  const [videoCallDidStart, setVideoCallDidStart] = useSessionFormState(
    'videoCallDidStart',
    savedCallReport?.detail2?.videoCallDidStart ?? undefined
  );
  const [videoIssues, setVideoIssues] = useSessionFormState(
    'videoCallIssues',
    savedCallReport?.detail2?.videoCallIssues ?? []
  );
  const [audioCallStarted, setAudioCallStarted] = useSessionFormState(
    'audioCallDidStart',
    savedCallReport?.detail2?.audioCallDidStart ?? undefined
  );
  const [audioCallIssues, setAudioCallIssues] = useSessionFormState(
    'audioCallIssues',
    savedCallReport?.detail2?.audioCallIssues ?? []
  );
  const [networkType, setNetworkType] = useSessionFormState(
    'networkType',
    savedCallReport?.detail2?.networkType ?? undefined
  );
  const [impact, setImpact] = useSessionFormState('impact', savedCallReport?.detail1 ?? undefined);

  const [overallFeedback, setOverallFeedback] = useState<string>('');
  const [aiIssueType, setAiIssueType] = useState<string>('');
  const [aiWorkflowImpact, setAiWorkflowImpact] = useState<string>('');
  const [timeSaved, setTimeSaved] = useState<string>('');
  const [aiNotes, setAiNotes] = useState<string>('');

  const formDisabled = appointment?.audit_appt_id || appointment?.appointment_completed || appointment?.read_only;
  const isAiFeedbackStarted = overallFeedback || aiIssueType || aiWorkflowImpact || timeSaved;
  const isOkDisabled =
    isSubmitting || formDisabled || !effectiveReportType || (effectiveReportType === 'ai' && !isAiFeedbackStarted);

  const handleOk = async () => {
    const missingFields = [];

    if (!effectiveReportType) {
      missingFields.push(t('Issue Type Selection'));
    } else if (effectiveReportType === 'call') {
      if (impact == null) missingFields.push(t('Impact'));
      if (isVideoSessionType && videoCallDidStart == null) missingFields.push(t('Video Call Started'));
      if (!isVideoSessionType && audioCallStarted == null) missingFields.push(t('Voice Call Started'));
      if (!networkType) missingFields.push(t('Network Type'));
    }

    if (missingFields.length > 0) {
      errorModal({
        title: t('Missing required fields'),
        content: `${t('Please fill in the following')}: ${missingFields.join(', ')}.`
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (effectiveReportType === 'call') {
        await sendTwilioEvents({
          userGuid: appointment.userId,
          appointmentId: appointment.appointment_id,
          event: 'RELIABILITY',
          message: impact,
          detail1: networkType,
          detail2: JSON.stringify({
            videoCallDidStart,
            videoCallIssues: videoIssues,
            audioCallDidStart: audioCallStarted,
            audioCallIssues
          })
        });
      } else {
        const success = aiWorkflowImpact === 'none' ? 1 : 0;

        await dispatch(
          actions.postAIFeedback(
            appointment.userId,
            appointment.appointment_id,
            sessionId || '',
            success,
            overallFeedback || '',
            aiIssueType || '',
            aiWorkflowImpact || '',
            timeSaved || '',
            aiNotes
          )
        );
      }

      refetch();
      setIsSubmitting(false);
      onClose();
    } catch {
      setIsSubmitting(false);
      errorModal({
        title: t('Error reporting issue'),
        content: t('There has been an error reporting your issue. Please try again.')
      });
    }
  };

  const radioVerticalStyle = { display: 'block', height: '30px', lineHeight: '30px' };
  const callText = savedCallReport !== undefined ? 'Update' : 'Submit';
  const okText = effectiveReportType === 'ai' ? 'Send feedback' : callText;

  return (
    <Modal
      title={heidiEnabled ? t('Feedback') : t('Report an issue')}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={okText}
      centered
      okButtonProps={{
        disabled: isOkDisabled,
        loading: isSubmitting
      }}
      cancelButtonProps={{ disabled: formDisabled }}
      width={600}
    >
      {heidiEnabled && (
        <FullWidthFormItem label={t('What is your feedback about?')} required layout="vertical">
          <Radio.Group value={reportType} onChange={(e) => setReportType(e.target.value)} disabled={formDisabled}>
            <Radio style={radioVerticalStyle} value="call">
              {t('Video or Audio calls')}
            </Radio>
            <Radio style={radioVerticalStyle} value="ai">
              {t('AI notes generation')}
            </Radio>
          </Radio.Group>
        </FullWidthFormItem>
      )}

      {effectiveReportType === 'call' && (
        <>
          {heidiEnabled && <Divider />}

          {isVideoSessionType ? (
            <>
              <h3>{t('Video Call')}</h3>
              <FullWidthFormItem label={t('Did your video call start?')} required layout="vertical">
                <Radio.Group
                  value={videoCallDidStart}
                  onChange={(e) => setVideoCallDidStart(e.target.value)}
                  disabled={formDisabled}
                >
                  <Radio value={true}>{t('Yes')}</Radio>
                  <Radio value={false}>{t('No')}</Radio>
                </Radio.Group>
              </FullWidthFormItem>

              {videoCallDidStart && (
                <FullWidthFormItem label={t('Video call issues')} layout="vertical">
                  <Checkbox.Group value={videoIssues} onChange={setVideoIssues} disabled={formDisabled}>
                    <StyledDiv>
                      <Checkbox value="seePatient">{t('Could you see the patient?')}</Checkbox>
                      <Checkbox value="patientSeeYou">{t('Could the patient see you?')}</Checkbox>
                      <Checkbox value="hearPatient">{t('Could you hear the patient?')}</Checkbox>
                      <Checkbox value="patientHearYou">{t('Could the patient hear you?')}</Checkbox>
                    </StyledDiv>
                  </Checkbox.Group>
                </FullWidthFormItem>
              )}
            </>
          ) : (
            <>
              <h3>{t('Voice Call')}</h3>
              <FullWidthFormItem label={t('Did your audio call start?')} required layout="vertical">
                <Radio.Group
                  value={audioCallStarted}
                  onChange={(e) => setAudioCallStarted(e.target.value)}
                  disabled={formDisabled}
                >
                  <Radio value={true}>{t('Yes')}</Radio>
                  <Radio value={false}>{t('No')}</Radio>
                </Radio.Group>
              </FullWidthFormItem>

              {audioCallStarted && (
                <FullWidthFormItem label={t('Audio call issues')} layout="vertical">
                  <Checkbox.Group value={audioCallIssues} onChange={setAudioCallIssues} disabled={formDisabled}>
                    <StyledDiv>
                      <Checkbox value="hearPatientVoice">{t('Could you hear the patient?')}</Checkbox>
                      <Checkbox value="patientHearYouVoice">{t('Could the patient hear you?')}</Checkbox>
                    </StyledDiv>
                  </Checkbox.Group>
                </FullWidthFormItem>
              )}
            </>
          )}

          <Divider />
          <h3>{t('Connection')}</h3>
          <FullWidthFormItem label={t('What network were you using?')} required layout="vertical">
            <Radio.Group value={networkType} onChange={(e) => setNetworkType(e.target.value)} disabled={formDisabled}>
              <Radio style={radioVerticalStyle} value="wifi">
                {t('Wi-Fi')}
              </Radio>
              <Radio style={radioVerticalStyle} value="mobile">
                {t('Mobile data')}
              </Radio>
              <Radio style={radioVerticalStyle} value="unknown">
                {t('Not sure')}
              </Radio>
            </Radio.Group>
          </FullWidthFormItem>

          <Divider />
          <FullWidthFormItem label={t('How much did this issue affect the consultation?')} required layout="vertical">
            <Radio.Group value={impact} onChange={(e) => setImpact(e.target.value)} disabled={formDisabled}>
              <Radio style={radioVerticalStyle} value="0">
                {t('Not at all')}
              </Radio>
              <Radio style={radioVerticalStyle} value="1">
                {t('Minor inconvenience')}
              </Radio>
              <Radio style={radioVerticalStyle} value="2">
                {t('Major disruption')}
              </Radio>
              <Radio style={radioVerticalStyle} value="3">
                {t('Could not complete consultation')}
              </Radio>
            </Radio.Group>
          </FullWidthFormItem>
        </>
      )}

      {effectiveReportType === 'ai' && (
        <>
          <Divider />
          <FullWidthFormItem label={t('Overall experience')} layout="vertical">
            <SmileyGroup
              value={overallFeedback}
              onChange={(e) => setOverallFeedback(e.target.value)}
              disabled={formDisabled}
            >
              {SMILEY_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  <div className="smiley-icon">
                    <img src={option.src} alt={option.alt} />
                  </div>
                </Radio>
              ))}
            </SmileyGroup>
          </FullWidthFormItem>

          <FullWidthFormItem label={t('Any issues with AI notes?')} layout="vertical">
            <Radio.Group value={aiIssueType} onChange={(e) => setAiIssueType(e.target.value)} disabled={formDisabled}>
              {AI_ISSUE_OPTIONS.map((opt) => (
                <Radio key={opt.value} style={radioVerticalStyle} value={opt.value}>
                  {t(opt.label)}
                </Radio>
              ))}
            </Radio.Group>
          </FullWidthFormItem>

          <FullWidthFormItem label={t('Did these issues affect your workflow?')} layout="vertical">
            <Radio.Group
              value={aiWorkflowImpact}
              onChange={(e) => setAiWorkflowImpact(e.target.value)}
              disabled={formDisabled}
            >
              {WORKFLOW_IMPACT_OPTIONS.map((opt) => (
                <Radio key={opt.value} style={radioVerticalStyle} value={opt.value}>
                  {t(opt.label)}
                </Radio>
              ))}
            </Radio.Group>
          </FullWidthFormItem>

          <FullWidthFormItem label={t('Did this still save you time?')} layout="vertical">
            <Radio.Group value={timeSaved} onChange={(e) => setTimeSaved(e.target.value)} disabled={formDisabled}>
              {TIME_SAVED_OPTIONS.map((opt) => (
                <Radio key={opt.value} style={radioVerticalStyle} value={opt.value}>
                  {t(opt.label)}
                </Radio>
              ))}
            </Radio.Group>
          </FullWidthFormItem>

          <FullWidthFormItem label={t('Additional comments')} layout="vertical">
            <TextArea
              rows={4}
              placeholder={t('Enter any additional details here...')}
              value={aiNotes}
              onChange={(e) => setAiNotes(e.target.value)}
              disabled={formDisabled}
            />
          </FullWidthFormItem>
        </>
      )}
    </Modal>
  );
};

export default ReportCallIssueModal;
